<template>
  <div>
    <tiptap-vuetify
      id="TextEditor"
      ref="tiptap"
      class="richtext"
      :class="maxheight"
      :value="value"
      @keydown="keydown"
      @input="(v) => $emit('input', v)"
      :extensions="extensions"
      :editor-properties="editorprops"
      placeholder="Text..."
    />
    <div v-if="allowImages && showImageHint" class="richtext-image-hint caption grey--text mt-1">
      Tip: click an image to resize (drag corner or use 100% / 50% / 33% / 25%). Use
      <strong>Insert 2 columns</strong> for equal columns, or <strong>Insert icon + text</strong> for a
      narrow icon on the left and content on the right. Upload the icon in the left cell, then type links
      in the right cell.
    </div>
  </div>
</template>

<script>
import {
  TiptapVuetify,
  Heading,
  Bold,
  Italic,
  Underline,
  BulletList,
  OrderedList,
  ListItem,
  Link,
  Blockquote,
  HardBreak,
  HorizontalRule,
  History,
} from "tiptap-vuetify";

import FileSelector from "./FileSelector.vue";
import ResizableImageExtension from "./ResizableImageExtension";
import ColumnExtension from "./ColumnExtension";
import TwoColumnsExtension from "./TwoColumnsExtension";

export default {
  props: {
    value: String,
    maxheight: String,
    allowImages: {
      type: Boolean,
      default: false,
    },
    showImageHint: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    TiptapVuetify,
  },
  data() {
    return {
      anchorTrack: 0,
      editorprops: {
        onTransaction: ({ state }) => {
          this.anchorTrack = state.selection.anchor;
        },
      },
    };
  },
  computed: {
    extensions() {
      const base = [
        History,
        [Heading, { options: { levels: [1, 2, 3] } }],
        Bold,
        Italic,
        Underline,
        Link,
        Blockquote,
        ListItem,
        BulletList,
        OrderedList,
        HorizontalRule,
        HardBreak,
        ColumnExtension,
        TwoColumnsExtension,
      ];

      if (this.allowImages) {
        base.push([
          ResizableImageExtension,
          {
            options: {
              imageSources: [{ component: FileSelector, name: "Upload" }],
              imageSourcesOverride: true,
            },
          },
        ]);
      }

      return base;
    },
  },
  methods: {
    keydown(e) {
      if (e.code !== "KeyV") return;
      if (!this.value.match(/\r?\n/)) return;
      e.stopPropagation();
      e.preventDefault();

      const pastedData = "";
      const fixedInput = pastedData.split(/\r?\n/).join("<br>");
      const value = this.value || "";
      this.$emit("input", value.slice(0, this.anchorTrack) + fixedInput + value.slice(this.anchorTrack));
    },
    handleNewLineCase(v) {
      this.$emit("input", v);
    },
  },
};
</script>

<style lang="scss">
.richtext {
  .v-card {
    box-shadow: none;
    border: 1px solid lightgrey !important;
  }

  .v-btn--icon.v-size--small {
    height: 20px;
    width: 20px;

    .v-icon {
      height: 20px;
      font-size: 20px;
      width: 20px;
    }
  }

  &.tiptap-vuetify-editor .ProseMirror {
    margin: 10px !important;

    img {
      max-width: 100%;
      height: auto;
      display: inline-block;
      vertical-align: top;
      object-fit: contain;
    }

    .rte-columns {
      display: flex;
      gap: 16px;
      width: 100%;
      margin: 12px 0;
      align-items: stretch;
      box-sizing: border-box;
    }

    .rte-columns--icon {
      align-items: flex-start;
    }

    .rte-column {
      flex: 1 1 0;
      min-width: 0;
      box-sizing: border-box;
      border: 1px dashed #cfcfcf;
      border-radius: 2px;
      padding: 8px;
      min-height: 48px;
    }

    .rte-columns--icon > .rte-column:first-child {
      flex: 0 0 72px;
      max-width: 72px;
      min-width: 72px;
      padding: 4px;
    }

    .rte-columns--icon > .rte-column:last-child {
      flex: 1 1 auto;
    }

    .rte-columns--icon > .rte-column:first-child img {
      width: 100%;
      max-width: 64px;
      height: auto;
      margin: 0;
    }
  }

  .tiptap-vuetify-editor__content {
    height: 300px;
    font-size: 11pt;
    font-family: "Calibri", sans-serif;
  }

  &.short .tiptap-vuetify-editor__content {
    height: 100px;
  }

  &.tall .tiptap-vuetify-editor__content {
    height: min(70vh, 720px);
    min-height: 520px;
  }
}

.richtext-image-hint {
  line-height: 1.3;
}
</style>
